"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Masonry from "react-masonry-css";
import { Button } from "@/components/ui/button";
import { NOT_IMAGE } from "@/lib/constants";
import type { ProductDetailsInfo } from "@/lib/bagisto/types";

const breakpointColumns = {
  default: 3,
  1024: 3,
  768: 2,
  640: 1,
};

interface GalleryGridProps {
  images: ProductDetailsInfo[];
}

const INITIAL_COUNT = 12;
const LOAD_MORE_COUNT = 6;

export default function GalleryGrid({ images }: GalleryGridProps) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const visibleImages = images.slice(0, visibleCount);
  const hasMore = visibleCount < images.length;

  return (
    <section id="gallery-grid" className="bg-background-50 px-4 sm:px-6 xl:px-10 py-[60px] xl:py-[120px]">
      <div className="w-full max-w-[1440px] mx-auto">

        {/* Heading */}
        <div className="flex flex-col items-center gap-4 xl:gap-[24px] text-center">
          <h2 className="font-oswald font-medium text-[28px] sm:text-[42px] xl:text-[60px] leading-[1.2] xl:leading-[80px] tracking-[0.2px] uppercase text-neutral-500">
            See How Guniwheel™ Works in Real-World Use
          </h2>
          <p className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] text-neutral-300 max-w-[90%] xl:max-w-[700px]">
            Discover real workshop photos and videos showcasing Guniwheel™ in action across various vehicles and repair environments. See how technicians use it for safe movement, storage, and efficient workflow every day.
          </p>
        </div>

        {/* Masonry Grid */}
        <Masonry
          breakpointCols={breakpointColumns}
          className="flex gap-4 xl:gap-6 w-full"
          columnClassName="flex flex-col gap-4 xl:gap-6"
        >
          {visibleImages.map((item) => (
            <div
              key={item.id}
              className="relative w-full rounded-[12px] overflow-hidden bg-background-50"
            >
              <Image
                src={item.images?.[0]?.url || NOT_IMAGE}
                alt={item.name}
                width={600}
                height={400}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="w-full h-auto object-cover rounded-[12px]"
              />
            </div>
          ))}
        </Masonry>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-[12px]">
          {hasMore && (
            <Button
              onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_COUNT)}
              className="w-full sm:w-auto h-[60px] px-[24px] py-[10px] rounded-[12px] bg-primary-500 hover:bg-primary-600 font-oswald font-medium text-[18px] leading-[100%] tracking-[0.2px] uppercase text-white cursor-pointer shadow-none"
            >
              SEE MORE PHOTOS
            </Button>
          )}
          <Button
            asChild
            className="w-full sm:w-auto h-[60px] px-[24px] py-[10px] rounded-[12px] bg-neutral-500 hover:bg-neutral-600 font-oswald font-medium text-[18px] leading-[100%] tracking-[0.2px] uppercase text-white cursor-pointer shadow-none"
          >
            <Link href="/products">SEE OUR PRODUCTS</Link>
          </Button>
        </div>

      </div>
    </section>
  );
}